import Papa from "papaparse";

// ============================================================
// Column mappings: varianti header italiano/inglese → nome interno
// ============================================================
const COLUMN_MAPPINGS = {
  // Data emissione
  "data emissione": "issueDate",
  "data": "issueDate",
  "date": "issueDate",
  "issuedate": "issueDate",
  "issue date": "issueDate",

  // Numero fattura
  "numero fattura": "invoiceCode",
  "numero": "invoiceCode",
  "n. fattura": "invoiceCode",
  "n.fattura": "invoiceCode",
  "number": "invoiceCode",
  "invoice number": "invoiceCode",
  "invoicecode": "invoiceCode",
  "invoice code": "invoiceCode",
  "codice fattura": "invoiceCode",

  // Cliente
  "cliente": "clientName",
  "client": "clientName",
  "clientname": "clientName",
  "client name": "clientName",
  "nome cliente": "clientName",
  "ragione sociale": "clientName",

  // P.IVA Cliente
  "p.iva cliente": "clientVat",
  "p.iva": "clientVat",
  "piva": "clientVat",
  "partita iva": "clientVat",
  "vat number": "clientVat",
  "vat": "clientVat",
  "cf cliente": "clientVat",
  "codice fiscale": "clientVat",

  // Importo lordo
  "importo lordo": "grossAmount",
  "importo": "grossAmount",
  "amount": "grossAmount",
  "grossamount": "grossAmount",
  "gross amount": "grossAmount",
  "totale": "grossAmount",

  // Incassata
  "incassata": "paid",
  "paid": "paid",
  "pagata": "paid",
  "stato pagamento": "paid",

  // Data pagamento
  "data pagamento": "paymentDate",
  "data incasso": "paymentDate",
  "payment date": "paymentDate",
  "paymentdate": "paymentDate",

  // Note
  "note": "notes",
  "notes": "notes",
  "descrizione": "notes",
  "description": "notes",

  // Email cliente → email
  "email_cliente": "clientEmail",
  "email cliente": "clientEmail",
  "email": "clientEmail",
  "e-mail": "clientEmail",

  // PEC cliente → pecEmail
  "pec_cliente": "clientPec",
  "pec cliente": "clientPec",
  "pec": "clientPec",

  // Telefono cliente → phone
  "telefono_cliente": "clientPhone",
  "telefono cliente": "clientPhone",
  "telefono": "clientPhone",
  "phone": "clientPhone",
  "tel": "clientPhone",

  // Referente → contactPerson
  "referente": "clientReferent",
  "contatto": "clientReferent",
  "contact person": "clientReferent",
  "contact": "clientReferent",
};

// Campi obbligatori (senza "paid" — ora opzionale)
const REQUIRED_FIELDS = ["issueDate", "invoiceCode", "clientName", "grossAmount"];

const FIELD_LABELS = {
  issueDate: "Data emissione",
  invoiceCode: "Numero fattura",
  clientName: "Cliente",
  clientVat: "P.IVA Cliente",
  grossAmount: "Importo lordo",
  paid: "Incassata",
  paymentDate: "Data pagamento",
  notes: "Note",
  clientEmail: "Email cliente",
  clientPec: "PEC cliente",
  clientPhone: "Telefono cliente",
  clientReferent: "Referente",
};

// ============================================================
// Parsing helpers
// ============================================================

export function toISODate(input) {
  const s = String(input || "").trim();
  if (!s) return "";

  // già ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // dd/mm/yy o dd/mm/yyyy o dd-mm-yy o dd-mm-yyyy
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (!m) return "";

  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  let yy = m[3];

  if (yy.length === 2) yy = String(2000 + Number(yy));

  const iso = `${yy}-${mm}-${dd}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (d.toISOString().slice(0, 10) !== iso) return "";

  return iso;
}

export function parseAmount(v) {
  if (v === null || v === undefined) return NaN;
  // gestisce "1.200,50" / "1200,50" / "1200" / "-3150"
  const s = String(v).trim().replace(/[€\s]/g, "").replace(/\./g, "").replace(",", ".");
  return Number(s);
}

export function parsePaid(v) {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "si" || s === "sì") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return null;
}

// Validazione base P.IVA italiana (11 cifre) o CF (16 alfanumerico)
export function validateVatOrCF(value) {
  if (!value) return null; // campo opzionale
  const s = String(value).trim().toUpperCase().replace(/^IT/, "");
  // P.IVA: 11 cifre
  if (/^\d{11}$/.test(s)) return s;
  // CF: 16 alfanumerico
  if (/^[A-Z0-9]{16}$/.test(s)) return s;
  return null;
}

// Normalizza gli header del CSV ai nomi interni
function normalizeRow(row) {
  const normalized = {};
  for (const [key, value] of Object.entries(row)) {
    const normalizedKey = key.toLowerCase().trim();
    const internalKey = COLUMN_MAPPINGS[normalizedKey];
    if (internalKey) {
      normalized[internalKey] = value;
    }
  }
  return normalized;
}

// ============================================================
// Row validation → { invoice, warnings[], errors[], isValid }
// ============================================================

export function validateRow(rawRow, lineNumber, existingClients = []) {
  const r = normalizeRow(rawRow);
  const warnings = [];
  const errors = [];

  // --- Campi obbligatori ---
  const issueDateISO = toISODate(r.issueDate);
  if (!issueDateISO) {
    errors.push(`${FIELD_LABELS.issueDate} non valida (usa dd/mm/yyyy)`);
  }

  const invoiceCode = (r.invoiceCode || "").trim();
  if (!invoiceCode) {
    errors.push(`${FIELD_LABELS.invoiceCode} obbligatorio`);
  }

  const clientName = (r.clientName || "").trim().replace(/\s+/g, " ");
  if (!clientName) {
    errors.push(`${FIELD_LABELS.clientName} obbligatorio`);
  }

  const amt = parseAmount(r.grossAmount);
  if (!Number.isFinite(amt) || amt === 0) {
    errors.push(`${FIELD_LABELS.grossAmount} non valido (diverso da 0)`);
  }

  // --- Incassata (opzionale) ---
  const hasPaidColumn = r.paid !== undefined && r.paid !== null && String(r.paid).trim() !== "";
  const paymentDateISO = toISODate(r.paymentDate);

  let paid;
  if (hasPaidColumn) {
    const parsedPaid = parsePaid(r.paid);
    if (parsedPaid === null) {
      errors.push(`${FIELD_LABELS.paid} deve essere si/no o true/false`);
      paid = false;
    } else {
      paid = parsedPaid;
    }
  } else {
    // Deduce da data pagamento
    paid = !!paymentDateISO;
    if (paid) {
      warnings.push("Colonna 'Incassata' assente — dedotta da Data pagamento");
    }
  }

  // --- Data pagamento ---
  if (r.paymentDate && String(r.paymentDate).trim() && !paymentDateISO) {
    errors.push(`${FIELD_LABELS.paymentDate} non valida (usa dd/mm/yyyy)`);
  }
  if (paid && !paymentDateISO) {
    warnings.push("Fattura incassata senza data pagamento");
  }

  // --- P.IVA (opzionale) ---
  const clientVat = (r.clientVat || "").trim();
  let validatedVat = "";
  if (clientVat) {
    validatedVat = validateVatOrCF(clientVat);
    if (!validatedVat) {
      warnings.push(`P.IVA/CF non valida: "${clientVat}"`);
      validatedVat = clientVat; // conserva il valore originale
    }
  } else {
    warnings.push("P.IVA Cliente mancante");
  }

  // --- Check cliente esistente ---
  let isNewClient = false;
  if (clientName && existingClients.length > 0) {
    const nameLC = clientName.toLowerCase();
    const vatClean = validatedVat ? validatedVat.replace(/^IT/i, "") : "";
    const found = existingClients.some((c) => {
      if ((c.name || "").toLowerCase() === nameLC) return true;
      if (vatClean && (c.vatNumber || "").replace(/^IT/i, "") === vatClean) return true;
      return false;
    });
    if (!found) {
      isNewClient = true;
    }
  }

  const notes = (r.notes || "").trim();
  const clientEmail = (r.clientEmail || "").trim();
  const clientPec = (r.clientPec || "").trim();
  const clientPhone = (r.clientPhone || "").trim();
  const clientReferent = (r.clientReferent || "").trim();
  const isValid = errors.length === 0;

  const invoice = isValid
    ? {
        issueDate: issueDateISO,
        invoiceCode,
        clientName,
        grossAmount: amt,
        paid,
        paymentDate: paid ? paymentDateISO : "",
        notes,
        clientVat: validatedVat,
      }
    : null;

  return {
    invoice,
    warnings,
    errors,
    isValid,
    lineNumber,
    isNewClient,
    clientName,
    clientVat: validatedVat,
    clientEmail,
    clientPec,
    clientPhone,
    clientReferent,
  };
}

// ============================================================
// processCSVFile — parsing completo + summary
// ============================================================

export function processCSVFile(fileOrContent, existingClients = []) {
  return new Promise((resolve, reject) => {
    const config = {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      delimiter: "",
      delimitersToGuess: [",", ";", "\t"],
      transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),

      complete: (results) => {
        const headers = results.meta.fields || [];

        // Mappa header trovati → nomi interni
        const foundInternalFields = new Set();
        headers.forEach((h) => {
          const normalizedKey = h.toLowerCase().trim();
          const internalKey = COLUMN_MAPPINGS[normalizedKey];
          if (internalKey) foundInternalFields.add(internalKey);
        });

        // Verifica campi obbligatori
        const missingFields = REQUIRED_FIELDS.filter((f) => !foundInternalFields.has(f));
        if (missingFields.length) {
          const missingLabels = missingFields.map((f) => FIELD_LABELS[f] || f);
          resolve({
            success: false,
            headerError: `Header CSV non valido. Mancano: ${missingLabels.join(", ")}`,
            rows: [],
            summary: null,
          });
          return;
        }

        const hasPaidColumn = foundInternalFields.has("paid");
        const hasVatColumn = foundInternalFields.has("clientVat");
        const rawRows = results.data || [];

        if (rawRows.length === 0) {
          resolve({
            success: false,
            headerError: "Il file CSV non contiene righe di dati.",
            rows: [],
            summary: null,
          });
          return;
        }

        // Valida ogni riga
        const validatedRows = rawRows.map((raw, idx) =>
          validateRow(raw, idx + 2, existingClients) // +1 header, +1 1-based
        );

        // Calcola summary
        const validCount = validatedRows.filter((r) => r.isValid && r.warnings.length === 0).length;
        const warningCount = validatedRows.filter((r) => r.isValid && r.warnings.length > 0).length;
        const errorCount = validatedRows.filter((r) => !r.isValid).length;
        const importableRows = validatedRows.filter((r) => r.isValid);
        const totalAmount = importableRows.reduce((sum, r) => sum + (r.invoice?.grossAmount || 0), 0);
        const paidCount = importableRows.filter((r) => r.invoice?.paid).length;
        const unpaidCount = importableRows.length - paidCount;

        // Clienti nuovi unici (dedup per nome case-insensitive, prima occorrenza vince sugli extra-field)
        const newClientsByKey = new Map();
        importableRows.forEach((r) => {
          if (!r.isNewClient || !r.clientName) return;
          const key = r.clientName.toLowerCase();
          if (newClientsByKey.has(key)) {
            // Arricchisce la prima occorrenza se mancano campi
            const existing = newClientsByKey.get(key);
            if (!existing.vatNumber && r.clientVat) existing.vatNumber = r.clientVat;
            if (!existing.email && r.clientEmail) existing.email = r.clientEmail;
            if (!existing.pecEmail && r.clientPec) existing.pecEmail = r.clientPec;
            if (!existing.phone && r.clientPhone) existing.phone = r.clientPhone;
            if (!existing.contactPerson && r.clientReferent) existing.contactPerson = r.clientReferent;
            return;
          }
          newClientsByKey.set(key, {
            name: r.clientName,
            vatNumber: r.clientVat || "",
            email: r.clientEmail || "",
            pecEmail: r.clientPec || "",
            phone: r.clientPhone || "",
            contactPerson: r.clientReferent || "",
          });
        });

        const newClients = Array.from(newClientsByKey.values());

        resolve({
          success: true,
          headerError: null,
          rows: validatedRows,
          hasPaidColumn,
          hasVatColumn,
          summary: {
            total: rawRows.length,
            validCount,
            warningCount,
            errorCount,
            importableCount: importableRows.length,
            totalAmount,
            paidCount,
            unpaidCount,
            newClients,
          },
        });
      },

      error: (err) => reject(err),
    };

    // Accetta File o stringa
    if (typeof fileOrContent === "string") {
      Papa.parse(fileOrContent, config);
    } else {
      Papa.parse(fileOrContent, config);
    }
  });
}

// ============================================================
// Template CSV
// ============================================================

export function generateCSVTemplate() {
  const BOM = "\uFEFF";
  const header = "Data emissione;Numero fattura;Cliente;P.IVA Cliente;Importo lordo;Incassata;Data pagamento;Note;email_cliente;pec_cliente;telefono_cliente;referente";
  const row1 = "15/01/2024;2024/001;Mario Rossi Srl;IT12345678901;1500,00;si;20/01/2024;Consulenza gennaio;mario@rossi.it;rossi@pec.it;0212345678;Luca Verdi";
  const row2 = "28/01/2024;2024/002;Acme Spa;IT98765432101;2500,00;no;;Progetto website;;;;";
  return BOM + [header, row1, row2].join("\n") + "\n";
}

export function downloadCSVTemplate() {
  const content = generateCSVTemplate();
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "template_fatture.csv";
  a.click();
  URL.revokeObjectURL(url);
}
